import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  decideApproval,
  getApprovalState,
  type ApprovalDecisionInput,
  type ApprovalState,
} from "@/api/approvals";
import { TASKS_KEY } from "@/hooks/useTasks";

export const APPROVALS_KEY = ["approvals"] as const;

/** GET /v1/approvals/tasks/:taskId — null when no flow has started. */
export function useApprovalState(
  taskId: string | null | undefined,
  enabled = true,
) {
  return useQuery<ApprovalState | null>({
    queryKey: [...APPROVALS_KEY, taskId],
    queryFn: () => getApprovalState(taskId as string),
    enabled: !!taskId && enabled,
    retry: false,
  });
}

/** POST /v1/approvals/tasks/:taskId/decide — approve/reject the current step. */
export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation<
    ApprovalState,
    unknown,
    { taskId: string; body: ApprovalDecisionInput }
  >({
    mutationFn: ({ taskId, body }) => decideApproval(taskId, body),
    onSuccess: (data) => {
      // Refresh the approval state + anything that keys off task status (the
      // task detail surfaces workflowEngReviewStatus, the lists show chips).
      qc.invalidateQueries({ queryKey: [...APPROVALS_KEY, data.taskId] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "detail", data.taskId] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "mine"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "queue"] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "assigned"] });
    },
  });
}
